import { Directive, ElementRef, OnDestroy, Renderer2, inject, input } from '@angular/core';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

/** Distance between the host element and the bubble, and the minimum gap to the viewport edge. */
const TOOLTIP_GAP = 8;
const VIEWPORT_MARGIN = 8;

let tooltipInstanceId = 0;
let activeTooltip: TooltipDirective | null = null;

/**
 * Styled replacement for the browser's native `title` tooltip.
 *
 * Deliberately rare: a tooltip earns its place only when the element cannot explain itself — an
 * icon-only control, an abbreviated value (the customers list collapses extra e-mails into "+2"),
 * or the reason something is unavailable. Repeating text that is already on screen is noise, which
 * is why ordinary table cells no longer carry one.
 *
 * Styles live in `src/styles.scss` (`.app-tooltip`), not here: the bubble is appended to
 * `document.body`, which puts it out of reach of any component's scoped styles.
 *
 * The bubble is appended to `document.body` and positioned `fixed` rather than nested in the host:
 * table rows sit inside scrollable, overflow-clipped containers where an absolutely positioned
 * child would be cut off at the container edge.
 */
@Directive({
  selector: '[appTooltip]',
  host: {
    '(mouseenter)': 'show()',
    '(mouseleave)': 'hide()',
    '(focusin)': 'showOnKeyboardFocus()',
    '(focusout)': 'hide()',
    // A row click navigates or opens a drawer; without this the bubble would outlive its host.
    '(click)': 'hide()',
    '(document:keydown.escape)': 'hide()',
  },
})
export class TooltipDirective implements OnDestroy {
  /** Bubble text. Blank renders nothing, so `[appTooltip]="value || ''"` is safe. */
  public readonly appTooltip = input('');
  public readonly tooltipPosition = input<TooltipPosition>('top');
  public readonly tooltipSingleLine = input(false);
  /** For an ellipsis-clipped host: show the bubble only while the text is actually cut off. */
  public readonly tooltipWhenTruncated = input(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);

  private bubble: HTMLElement | null = null;
  private bubbleId = '';

  // Any scroll hides the bubble, including scrolling inside the table's own container — that never
  // reaches `window`, so the listener is registered in the capture phase on `document`.
  private readonly onScrollOrResize = (): void => this.hide();

  public ngOnDestroy(): void {
    this.hide();
  }

  /**
   * Programmatic focus restoration (for example, after a drawer closes) must not resurrect a
   * tooltip that the click already dismissed. `:focus-visible` keeps the accessible keyboard-focus
   * behavior while ignoring focus restored after a pointer interaction.
   */
  protected showOnKeyboardFocus(): void {
    if (this.host.nativeElement.matches(':focus-visible')) {
      this.show();
    }
  }

  protected show(): void {
    const text = this.appTooltip().trim();
    if (!text || this.bubble) {
      return;
    }
    const host = this.host.nativeElement;
    if (this.tooltipWhenTruncated() && host.scrollWidth <= host.clientWidth) {
      return;
    }

    activeTooltip?.hide();
    activeTooltip = this;

    this.bubbleId = `app-tooltip-${(tooltipInstanceId += 1)}`;

    const bubble = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(bubble, 'app-tooltip');
    if (this.tooltipSingleLine()) {
      this.renderer.addClass(bubble, 'is-single-line');
    }
    this.renderer.setAttribute(bubble, 'id', this.bubbleId);
    this.renderer.setAttribute(bubble, 'role', 'tooltip');
    this.renderer.setProperty(bubble, 'textContent', text);
    this.renderer.appendChild(document.body, bubble);
    this.bubble = bubble;

    // The host keeps its own aria-label where it has one; this only adds the extra description.
    this.renderer.setAttribute(this.host.nativeElement, 'aria-describedby', this.bubbleId);

    this.position(bubble);

    document.addEventListener('scroll', this.onScrollOrResize, true);
    window.addEventListener('resize', this.onScrollOrResize);
  }

  protected hide(): void {
    if (!this.bubble) {
      return;
    }

    document.removeEventListener('scroll', this.onScrollOrResize, true);
    window.removeEventListener('resize', this.onScrollOrResize);

    this.renderer.removeChild(document.body, this.bubble);
    this.renderer.removeAttribute(this.host.nativeElement, 'aria-describedby');
    this.bubble = null;
    this.bubbleId = '';
    if (activeTooltip === this) {
      activeTooltip = null;
    }
  }

  /** Places the bubble beside the host, flipping and clamping so it stays on screen. */
  private position(bubble: HTMLElement): void {
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const { width, height } = bubble.getBoundingClientRect();
    const requested = this.tooltipPosition();

    if (requested === 'left' || requested === 'right') {
      // Side placement flips to the other side when the requested one would run off screen.
      const fitsLeft = hostRect.left - width - TOOLTIP_GAP >= VIEWPORT_MARGIN;
      const placeLeft = requested === 'left' ? fitsLeft : false;
      const left = placeLeft ? hostRect.left - width - TOOLTIP_GAP : hostRect.right + TOOLTIP_GAP;
      const middle = hostRect.top + hostRect.height / 2 - height / 2;
      const maxTop = window.innerHeight - height - VIEWPORT_MARGIN;

      this.renderer.addClass(bubble, placeLeft ? 'is-left' : 'is-right');
      this.renderer.setStyle(bubble, 'left', `${Math.round(this.clamp(left, window.innerWidth - width))}px`);
      this.renderer.setStyle(bubble, 'top', `${Math.round(this.clamp(middle, maxTop + VIEWPORT_MARGIN))}px`);
      this.renderer.addClass(bubble, 'is-visible');

      return;
    }

    const fitsAbove = hostRect.top - height - TOOLTIP_GAP >= VIEWPORT_MARGIN;
    const placeAbove = requested === 'top' ? fitsAbove : false;
    const top = placeAbove ? hostRect.top - height - TOOLTIP_GAP : hostRect.bottom + TOOLTIP_GAP;
    const centred = hostRect.left + hostRect.width / 2 - width / 2;

    this.renderer.addClass(bubble, placeAbove ? 'is-above' : 'is-below');
    this.renderer.setStyle(bubble, 'top', `${Math.round(top)}px`);
    this.renderer.setStyle(bubble, 'left', `${Math.round(this.clamp(centred, window.innerWidth - width))}px`);
    // Painted only once positioned, so it never flashes at the top-left corner first.
    this.renderer.addClass(bubble, 'is-visible');
  }

  /** Keeps a computed offset inside the viewport, never pushing it past the near edge. */
  private clamp(value: number, max: number): number {
    return Math.max(VIEWPORT_MARGIN, Math.min(value, Math.max(VIEWPORT_MARGIN, max - VIEWPORT_MARGIN)));
  }
}
